import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchPicks,
  pickLine,
  unpickMovement,
} from "../../../../../../../services/ShipperServices/DeliveryRoutes/picks.services";

// GET wms/picks takes up to this many orders at a time.
const PAGE = 100;
// Answers meaning this Bolt has no picks (Bolt Locations not there yet), or
// this login can't see them: the route sheet then works as before.
const UNAVAILABLE = new Set([401, 403, 404, 405]);

const messageOf = (error, fallback) =>
  error?.response?.data?.message || fallback;

/**
 * What is picked on the route sheet's orders, from Bolt: the same picks the
 * warehouse app makes, so a tick shows on every phone and in the app.
 *
 * `status`: "off" (not asked yet), "loading", "ready", or "unavailable" (no
 * picks in this Bolt, or this login can't see them: no tick boxes, and the
 * pick list keeps its ticks on this device as before).
 *
 * `toggle(order, item)`: a line not fully picked is picked (what is left,
 * with no spot); a picked line is unpicked (its ticks: picks with no spot;
 * a pick taken off a spot in the app is undone in the app, and what the
 * website's Fulfilled step took off a pallet can't be).
 * `toggleLines(entries)` does the same for a pick list row ({ order, item }
 * per line): all picked → all unpicked, else the rest picked.
 * `pickLines(entries, pick)` picks or unpicks those lines whatever they show.
 * A line already being saved is left alone, so a second tap can't send it
 * twice. `busy` is the set of line ids being saved.
 *
 * A first read with no answer (offline, a server error) quietly leaves the
 * ticks on this device; a read after a change that fails says so.
 */
export default function useRoutePicks(orders, enabled) {
  const [status, setStatus] = useState("off");
  const [lines, setLines] = useState({});
  const [busy, setBusy] = useState(() => new Set());
  const [message, setMessage] = useState("");
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const statusRef = useRef(status);
  statusRef.current = status;
  // The lines being saved, as of now (state lags behind a second tap).
  const busyRef = useRef(new Set());

  const ids = useMemo(
    () => (orders ?? []).map((order) => Number(order?.id)).filter(Boolean),
    [orders],
  );
  const idsKey = ids.join(",");

  const load = useCallback(async (orderIds) => {
    if (!orderIds.length) return true;
    const found = {};
    for (let start = 0; start < orderIds.length; start += PAGE) {
      const { data, error } = await fetchPicks(
        orderIds.slice(start, start + PAGE),
      );
      if (error) {
        if (UNAVAILABLE.has(error?.response?.status)) {
          setStatus("unavailable");
          return false;
        }
        if (statusRef.current === "ready") {
          setMessage(
            messageOf(error, "Couldn't read what is picked. Try again."),
          );
        } else {
          setStatus("unavailable");
        }
        return false;
      }
      (data?.result?.orders ?? []).forEach((order) => {
        (order?.lines ?? []).forEach((line) => {
          found[String(line.order_detail_id)] = {
            ...line,
            orderId: Number(order.id),
            warehouseId: order.warehouse_id,
          };
        });
      });
    }
    const asked = new Set(orderIds.map(Number));
    setLines((current) => {
      const next = {};
      Object.entries(current).forEach(([lineId, line]) => {
        if (!asked.has(line.orderId)) next[lineId] = line;
      });
      return { ...next, ...found };
    });
    setStatus("ready");
    return true;
  }, []);

  useEffect(() => {
    if (!enabled || !idsKey) return;
    if (statusRef.current === "off") setStatus("loading");
    load(idsKey.split(",").map(Number));
  }, [enabled, idsKey, load]);

  const lineOf = useCallback((lineId) => lines[String(lineId)], [lines]);
  const isPicked = useCallback(
    (lineId) => {
      const line = lines[String(lineId)];
      return Boolean(line && line.ordered > 0 && line.remaining <= 0);
    },
    [lines],
  );
  const isBusy = useCallback((lineId) => busy.has(String(lineId)), [busy]);

  const mark = (lineIds, on) => {
    lineIds.forEach((id) =>
      on ? busyRef.current.add(String(id)) : busyRef.current.delete(String(id)),
    );
    setBusy(new Set(busyRef.current));
  };

  // Picks what is left of one line, or unpicks it. Returns an error message or "".
  const change = async (order, item, pick) => {
    const line = linesRef.current[String(item?.id)];
    const warehouseId = line?.warehouseId ?? order?.warehouse_id;
    if (!line || !warehouseId) return "This item can't be picked here";
    if (pick) {
      if (line.remaining <= 0) return "";
      const { error } = await pickLine(
        order.id,
        warehouseId,
        item.id,
        line.remaining,
      );
      return error
        ? messageOf(error, "Couldn't save the pick. Try again.")
        : "";
    }
    // A tick undoes ticks: picks with no spot. A pick taken off a spot in
    // the warehouse app is undone there (it goes back to its spot), and
    // what the website's Fulfilled step took off a pallet stays.
    const undoable = (line.picks ?? []).filter(
      (each) => each.source !== "web" && !each.located,
    );
    if (!undoable.length) {
      return (line.picks ?? []).some((each) => each.source !== "web")
        ? "Picked from a spot in the warehouse app: undo it in the app"
        : "Taken off a pallet by the website's Fulfilled step: it can't be unticked here";
    }
    for (const each of undoable) {
      const { error } = await unpickMovement(
        order.id,
        warehouseId,
        each.movement_id,
      );
      if (error) return messageOf(error, "Couldn't undo the pick. Try again.");
    }
    return "";
  };

  const run = async (wanted, pick) => {
    const entries = wanted.filter(
      ({ item }) => !busyRef.current.has(String(item?.id)),
    );
    if (!entries.length) return;
    const lineIds = entries.map(({ item }) => item?.id);
    mark(lineIds, true);
    setMessage("");
    let problem = "";
    try {
      for (const { order, item } of entries) {
        problem = (await change(order, item, pick)) || problem;
      }
    } finally {
      await load([...new Set(entries.map(({ order }) => Number(order.id)))]);
      mark(lineIds, false);
    }
    if (problem) setMessage(problem);
  };

  const toggle = (order, item) => run([{ order, item }], !isPicked(item?.id));
  const toggleLines = (entries) =>
    run(entries, !entries.every(({ item }) => isPicked(item?.id)));
  const pickLines = (entries, pick) => run(entries, pick);
  const reload = (orderIds) =>
    statusRef.current === "ready"
      ? load(orderIds.map(Number))
      : Promise.resolve(false);

  return {
    status,
    ready: status === "ready",
    lineOf,
    isPicked,
    isBusy,
    toggle,
    toggleLines,
    pickLines,
    busy,
    reload,
    message,
    clearMessage: () => setMessage(""),
  };
}
